import { useEffect, useState } from "react";
import { Loader2, Bell, Mail, MessageCircle, AlertTriangle, Save, BellRing, Send } from "lucide-react";
import api from "@/lib/api";
import { errMsg } from "@/lib/format";
import { toast } from "sonner";
import usePushNotifications from "@/hooks/usePushNotifications";

/**
 * Notification preferences page.
 *
 * Lets the user opt-in to email/WhatsApp alerts (when those integrations get
 * wired up). The toggles are saved to the user's profile; the actual sending
 * is gated by RESEND_API_KEY / Twilio creds on the backend.
 */
const ALERTS = [
  { key: "budget", label: "Alertas de presupuesto", desc: "Avísame cuando llegue al 90% de un presupuesto." },
  { key: "cards", label: "Recordatorios de tarjetas", desc: "3 días antes del corte y del pago mínimo." },
  { key: "debts", label: "Vencimientos de deudas", desc: "Aviso 3 días antes de cada cuota." },
  { key: "recurring", label: "Cargos recurrentes", desc: "Recordatorio el día del cargo programado." },
  { key: "subscriptions", label: "Detecciones automáticas", desc: "Cuando detectemos una suscripción nueva no trackeada." },
  { key: "summary", label: "Resumen mensual", desc: "Reporte branded el día 1 de cada mes." },
];

export default function NotificationsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const push = usePushNotifications();
  const [prefs, setPrefs] = useState({
    email_enabled: true,
    push_enabled: true,
    whatsapp_enabled: false,
    whatsapp_number: "",
    preferred_hour: 9,
    alerts: ALERTS.reduce((acc, a) => ({ ...acc, [a.key]: true }), {}),
  });

  useEffect(() => {
    api.get("/notifications/preferences")
      .then(r => {
        const p = r.data || {};
        setPrefs(prev => ({
          ...prev,
          ...p,
          alerts: { ...prev.alerts, ...(p.alerts || {}) },
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", prefs);
      toast.success("Preferencias guardadas");
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const toggleAlert = (k) => setPrefs(p => ({ ...p, alerts: { ...p.alerts, [k]: !p.alerts[k] } }));

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-violet-400" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-5 sm:p-6 space-y-6" data-testid="notifications-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] text-violet-300 font-bold">PERFIL</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-bold">Notificaciones</h1>
        <p className="text-sm text-slate-400 mt-1">Decide qué alertas quieres recibir y por qué canal.</p>
      </header>

      {/* Channels */}
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 space-y-4" data-testid="channels-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">Canales</h2>

        {/* Push notifications — native browser push (PWA) */}
        <div className="space-y-2">
          <ToggleRow
            icon={BellRing}
            label="Notificaciones del navegador (PWA)"
            desc={
              !push.supported
                ? "Este navegador no soporta notificaciones push."
                : push.permission === "denied"
                ? "Bloqueadas por el navegador — actívalas desde el candado en la barra de URL."
                : push.subscribed
                ? "Activas en este dispositivo."
                : "Recibe alertas directo en tu Android/iOS/PC sin instalar app."
            }
            checked={prefs.push_enabled && push.subscribed}
            disabled={!push.supported || push.permission === "denied" || push.busy}
            onChange={async (v) => {
              if (v) {
                const ok = await push.enable();
                if (ok) {
                  setPrefs((p) => ({ ...p, push_enabled: true }));
                  toast.success("Push notifications activadas");
                } else if (push.permission === "denied") {
                  toast.error("Permiso denegado en el navegador");
                } else {
                  toast.error("No se pudo activar");
                }
              } else {
                await push.disable();
                setPrefs((p) => ({ ...p, push_enabled: false }));
                toast.success("Push notifications desactivadas");
              }
            }}
            testid="toggle-push"
          />
          {push.subscribed && (
            <div className="ml-12">
              <button
                onClick={async () => {
                  const n = await push.sendTest();
                  if (n > 0) toast.success(`Prueba enviada a ${n} dispositivo${n > 1 ? "s" : ""}`);
                  else toast.error("No se pudo enviar la prueba");
                }}
                data-testid="push-test-btn"
                className="text-xs px-3 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-200 hover:bg-violet-500/25 inline-flex items-center gap-2"
              >
                <Send className="w-3 h-3" /> Enviar notificación de prueba
              </button>
            </div>
          )}
        </div>

        <ToggleRow
          icon={Mail}
          label="Correo electrónico"
          desc="Te llegarán a tu email registrado."
          checked={prefs.email_enabled}
          onChange={(v) => setPrefs(p => ({ ...p, email_enabled: v }))}
          testid="toggle-email"
        />

        <ToggleRow
          icon={MessageCircle}
          label="WhatsApp"
          desc="Próximamente — requiere número verificado."
          checked={prefs.whatsapp_enabled}
          onChange={(v) => setPrefs(p => ({ ...p, whatsapp_enabled: v }))}
          testid="toggle-whatsapp"
        />

        {prefs.whatsapp_enabled && (
          <div className="ml-12 -mt-2">
            <input
              type="tel"
              value={prefs.whatsapp_number || ""}
              onChange={(e) => setPrefs(p => ({ ...p, whatsapp_number: e.target.value }))}
              placeholder="+1 829 555 5555"
              data-testid="whatsapp-number-input"
              className="w-full sm:w-72 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
        )}

        <div className="ml-12 flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Hora preferida</span>
          <select
            value={prefs.preferred_hour}
            onChange={(e) => setPrefs(p => ({ ...p, preferred_hour: Number(e.target.value) }))}
            data-testid="preferred-hour-select"
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm"
          >
            {Array.from({ length: 24 }).map((_, h) => (
              <option key={h} value={h} className="bg-[#15141b]">
                {`${String(h).padStart(2, "0")}:00`}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Alerts */}
      <section className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 space-y-2" data-testid="alerts-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 mb-2">Tipos de alerta</h2>
        {ALERTS.map((a) => (
          <ToggleRow
            key={a.key}
            icon={Bell}
            label={a.label}
            desc={a.desc}
            checked={!!prefs.alerts[a.key]}
            onChange={() => toggleAlert(a.key)}
            testid={`toggle-alert-${a.key}`}
            compact
          />
        ))}
      </section>

      <div className="rounded-xl bg-violet-500/10 border border-violet-500/30 p-3 flex gap-2 items-start" data-testid="multi-channel-notice">
        <AlertTriangle className="w-4 h-4 text-violet-300 mt-0.5 shrink-0" />
        <p className="text-[12px] text-violet-200 leading-relaxed">
          Las notificaciones push del navegador funcionan en cualquier dispositivo (Android, iOS PWA, PC). Si las activas, recibirás alertas instantáneas sin depender de email.
        </p>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="notifications-save-btn"
        className="flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Guardar preferencias
      </button>
    </div>
  );
}

function ToggleRow({ icon: Icon, label, desc, checked, onChange, testid, compact, disabled }) {
  return (
    <label className={`flex items-center gap-3 ${disabled ? "opacity-60" : "cursor-pointer"} ${compact ? "py-2" : "py-1"}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
        checked ? "bg-violet-500/20 text-violet-300" : "bg-white/5 text-slate-500"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-[12px] text-slate-400">{desc}</div>
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors border-0 p-0 ${checked ? "bg-violet-500" : "bg-white/15"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent`}
        aria-pressed={checked}
      >
        <span
          className={`absolute top-1/2 -translate-y-1/2 left-0 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </label>
  );
}
